import { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2, 
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw,
  Sparkles, CalendarClock, Ticket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { BackstagePanel } from '@/components/BackstagePanel';
import { DomainConnect } from '@/components/DomainConnect';
import { SEOSettings } from '@/components/SEOSettings';

// Module editors - extracted into individual files
import { HomeModuleEditor } from '@/components/editors/HomeModuleEditor';
import { AboutModuleEditor } from '@/components/editors/AboutModuleEditor';
import { ContactModuleEditor } from '@/components/editors/ContactModuleEditor';
import { GalleryModuleEditor } from '@/components/editors/GalleryModuleEditor';
import { VideoGalleryModuleEditor } from '@/components/editors/VideoGalleryModuleEditor';
import { TimetableModuleEditor } from '@/components/editors/TimetableModuleEditor';
import { NewsModuleEditor } from '@/components/editors/NewsModuleEditor';
import { EventsModuleEditor } from '@/components/editors/EventsModuleEditor';
import { SocialLinksEditor } from '@/components/editors/SocialLinksEditor';
import { MusicModuleEditor } from '@/components/editors/MusicModuleEditor';
import { MailingListModuleEditor } from '@/components/editors/MailingListModuleEditor';
import { PressModuleEditor } from '@/components/editors/PressModuleEditor';
import { ServicesModuleEditor } from '@/components/editors/ServicesModuleEditor';
import { TeamModuleEditor } from '@/components/editors/TeamModuleEditor';
import { FAQModuleEditor } from '@/components/editors/FAQModuleEditor';
import { TestimonialsModuleEditor } from '@/components/editors/TestimonialsModuleEditor';
import { RetreatsModuleEditor } from '@/components/editors/RetreatsModuleEditor';

// Module icons and metadata
const moduleIcons = {
  home: Layout,
  about: User,
  gallery: Image,
  video_gallery: Video,
  news: Newspaper,
  events: Calendar,
  contact: Mail,
  music: Music,
  social_links: ExternalLink,
  mailing_list: Mail,
  press: Quote,
  services: Briefcase,
  team: Users,
  faq: HelpCircle,
  testimonials: MessageSquare,
  timetable: CalendarClock,
  retreats: Ticket,
};


// ─── Domain Settings Panel ───
const DomainSettings = ({ siteData, onUpdate }) => {
  const [domain, setDomain] = useState(siteData.custom_domain || '');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dnsStatus, setDnsStatus] = useState(null); // null, 'checking', 'ok', 'fail'
  const [copied, setCopied] = useState(false);
  const [autoDomains, setAutoDomains] = useState(null); // null = loading, true/false

  useEffect(() => {
    api.get('/domains/config')
      .then(res => setAutoDomains(res.data.configured))
      .catch(() => setAutoDomains(false));
  }, []);

  const BOXIT_CNAME = 'boxit.website';

  const handleSaveDomain = async () => {
    setSaving(true);
    try {
      const cleaned = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
      await api.put(`/sites/${siteData.id}`, { custom_domain: cleaned || null });
      setDomain(cleaned);
      onUpdate({ custom_domain: cleaned || null });
      toast.success(cleaned ? 'Domain saved' : 'Domain removed');
      setDnsStatus(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDns = async () => {
    setVerifying(true);
    setDnsStatus('checking');
    try {
      const res = await api.post(`/sites/${siteData.id}/verify-domain`);
      setDnsStatus(res.data.dns_configured ? 'ok' : 'fail');
    } catch {
      setDnsStatus('fail');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-8" data-testid="domain-settings">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Custom Domain</h3>
            <p className="text-sm text-slate-400">
              {autoDomains ? 'Connect your own domain — just change your nameservers' : 'Connect your own domain to this site'}
            </p>
          </div>
        </div>

        {/* Automated Cloudflare flow */}
        {autoDomains && (
          <>
            <DomainConnect siteData={siteData} onUpdate={onUpdate} />
            {/* Boxit URL */}
            <div className="flex items-center gap-2 text-sm pt-4">
              <span className="text-slate-500">Boxit URL:</span>
              <code className="text-slate-300 font-mono text-xs bg-slate-800 px-2 py-1 rounded">
                {window.location.origin}/sites/{siteData.slug}
              </code>
            </div>
          </>
        )}

        {/* Manual fallback (Cloudflare not configured) */}
        {autoDomains === false && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="e.g. timhowar.com"
                data-testid="custom-domain-input"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
            <Button
              onClick={handleSaveDomain}
              disabled={saving}
              data-testid="save-domain-btn"
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-5"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>

          {/* Current domain display */}
          {siteData.custom_domain && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">Active domain:</span>
              <a href={`https://${siteData.custom_domain}`} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 font-medium">
                {siteData.custom_domain}
              </a>
              <Button variant="ghost" size="sm" onClick={handleVerifyDns} disabled={verifying} data-testid="verify-dns-btn" className="text-xs text-slate-400 hover:text-white h-7 px-2">
                <RefreshCw className={cn("w-3 h-3 mr-1", verifying && "animate-spin")} />
                {verifying ? 'Checking...' : 'Verify DNS'}
              </Button>
              {dnsStatus === 'ok' && <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3 h-3" /> Connected</span>}
              {dnsStatus === 'fail' && <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertTriangle className="w-3 h-3" /> DNS not detected</span>}
            </div>
          )}

          {/* DNS Instructions */}
          {siteData.custom_domain && (
            <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 space-y-3">
              <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                <Settings className="w-4 h-4 text-indigo-400" />
                DNS Setup Instructions
              </h4>
              <p className="text-xs text-slate-400">Add these records at your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.):</p>

              <div className="space-y-2">
                {/* CNAME Record */}
                <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">CNAME Record</span>
                    <button 
                      onClick={() => copyToClipboard(BOXIT_CNAME)}
                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                      data-testid="copy-cname-btn"
                    >
                      <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-slate-500">Type</span>
                      <p className="text-white font-mono mt-0.5">CNAME</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Name / Host</span>
                      <p className="text-white font-mono mt-0.5">@</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Value / Points to</span>
                      <p className="text-indigo-300 font-mono mt-0.5">{BOXIT_CNAME}</p>
                    </div>
                  </div>
                </div>

                {/* WWW Record */}
                <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">WWW Redirect</span>
                  <div className="grid grid-cols-3 gap-4 text-xs mt-1">
                    <div>
                      <span className="text-slate-500">Type</span>
                      <p className="text-white font-mono mt-0.5">CNAME</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Name / Host</span>
                      <p className="text-white font-mono mt-0.5">www</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Value / Points to</span>
                      <p className="text-indigo-300 font-mono mt-0.5">{BOXIT_CNAME}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-xs text-indigo-200 space-y-1.5">
                <p><strong>Option A — Cloudflare (Recommended):</strong> Add your domain to Cloudflare (free), set the CNAME records above, and Cloudflare handles SSL automatically.</p>
                <p><strong>Option B — Direct DNS:</strong> Point your CNAME to <span className="font-mono text-indigo-300">{BOXIT_CNAME}</span>. SSL will need to be configured on the server.</p>
                <p className="text-slate-400">DNS changes can take up to 24 hours to propagate. Click "Verify DNS" to check status.</p>
              </div>
            </div>
          )}

          {/* Boxit URL */}
          <div className="flex items-center gap-2 text-sm pt-1">
            <span className="text-slate-500">Boxit URL:</span>
            <code className="text-slate-300 font-mono text-xs bg-slate-800 px-2 py-1 rounded">
              {window.location.origin}/sites/{siteData.slug}
            </code>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};


export default function SiteEditor({ site, onClose }) {
  const [siteData, setSiteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null); // Start with dashboard view
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [modifiedModules, setModifiedModules] = useState(new Set());
  const [backstageOpen, setBackstageOpen] = useState(false);

  // Fetch fresh site data when editor opens
  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const response = await api.get(`/sites/${site.id}`);
        setSiteData(response.data);
      } catch (error) {
        console.error('Failed to load site:', error);
        toast.error('Failed to load site data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSiteData();
  }, [site.id]);

  const handleModuleChange = (moduleName, newData) => {
    setSiteData(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleName]: newData
      }
    }));
    setModifiedModules(prev => new Set([...prev, moduleName]));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only save modules that have been modified
      for (const moduleName of modifiedModules) {
        const moduleData = siteData.modules?.[moduleName];
        if (moduleData) {
          await api.put(`/sites/${siteData.id}/modules/${moduleName}`, moduleData);
        }
      }
      toast.success('Site saved successfully');
      setHasChanges(false);
      setModifiedModules(new Set());
    } catch (error) {
      toast.error('Failed to save site');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (isLoading || !siteData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-slate-400 text-sm">Loading site data...</p>
        </div>
      </div>
    );
  }

  const handlePublish = async () => {
    try {
      await api.post(`/sites/${siteData.id}/publish`);
      setSiteData(prev => ({...prev, status: 'published'}));
      toast.success('Site published!');
    } catch (error) {
      toast.error('Failed to publish site');
    }
  };

  const handleUnpublish = async () => {
    try {
      await api.post(`/sites/${siteData.id}/unpublish`);
      setSiteData(prev => ({...prev, status: 'draft'}));
      toast.success('Site unpublished');
    } catch (error) {
      toast.error('Failed to unpublish site');
    }
  };

  // Apply actions from Backstage AI
  const handleApplyAIActions = (actions) => {
    if (!actions?.length) return;
    setSiteData(prev => {
      const updated = { ...prev, modules: { ...prev.modules } };
      const touched = new Set(modifiedModules);

      for (const action of actions) {
        const mod = action.module;
        if (!mod) continue;
        const currentMod = { ...(updated.modules[mod] || {}) };

        if (action.type === 'update_field' && action.field && action.value !== undefined) {
          currentMod[action.field] = action.value;
          updated.modules[mod] = currentMod;
          touched.add(mod);
        } else if (action.type === 'add_item' && action.item) {
          const item = { id: crypto.randomUUID?.() || Date.now().toString(), ...action.item };
          // Determine list key based on module
          const listKey = mod === 'news' ? 'items' : mod === 'events' ? 'events'
            : mod === 'services' ? 'services' : mod === 'faq' ? 'faqs'
            : mod === 'team' ? 'members' : mod === 'testimonials' ? 'testimonials'
            : mod === 'gallery' ? 'images' : mod === 'press' ? 'quotes' : null;
          if (listKey) {
            currentMod[listKey] = [...(currentMod[listKey] || []), item];
            updated.modules[mod] = currentMod;
            touched.add(mod);
          }
        } else if (action.type === 'remove_item' && action.item_id) {
          const listKey = mod === 'news' ? 'items' : mod === 'events' ? 'events'
            : mod === 'services' ? 'services' : mod === 'faq' ? 'faqs'
            : mod === 'team' ? 'members' : mod === 'testimonials' ? 'testimonials'
            : mod === 'gallery' ? 'images' : mod === 'press' ? 'quotes' : null;
          if (listKey && currentMod[listKey]) {
            currentMod[listKey] = currentMod[listKey].filter(i => i.id !== action.item_id);
            updated.modules[mod] = currentMod;
            touched.add(mod);
          }
        }
      }

      setModifiedModules(touched);
      setHasChanges(true);
      return updated;
    });
  };

  const getModuleEditor = (moduleName) => {
    const data = siteData.modules?.[moduleName] || {};
    const onChange = (newData) => handleModuleChange(moduleName, newData);
    const siteSlug = siteData.slug;

    switch (moduleName) {
      case 'home': return <HomeModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'about': return <AboutModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'contact': return <ContactModuleEditor data={data} onChange={onChange} />;
      case 'gallery': return <GalleryModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'video_gallery': return <VideoGalleryModuleEditor data={data} onChange={onChange} />;
      case 'news': return <NewsModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'events': return <EventsModuleEditor data={data} onChange={onChange} siteId={siteData.id} sitePackage={siteData.package} />;
      case 'social_links': return <SocialLinksEditor data={data} onChange={onChange} />;
      case 'music': return <MusicModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'mailing_list': return <MailingListModuleEditor data={data} onChange={onChange} siteId={siteData.id} />;
      case 'press': return <PressModuleEditor data={data} onChange={onChange} />;
      case 'services': return <ServicesModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'team': return <TeamModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'faq': return <FAQModuleEditor data={data} onChange={onChange} />;
      case 'testimonials': return <TestimonialsModuleEditor data={data} onChange={onChange} siteSlug={siteSlug} />;
      case 'timetable': return <TimetableModuleEditor data={data} onChange={onChange} />;
      case 'retreats': return <RetreatsModuleEditor data={data} onChange={onChange} siteId={siteData.id} stripeKey={siteData.stripe_api_key} />;
      default: return <p className="text-slate-400">Editor not available for {moduleName}</p>;
    }
  };

  // Get module status/preview info
  const getModuleStatus = (moduleName) => {
    const data = siteData.modules?.[moduleName] || {};
    switch (moduleName) {
      case 'home':
        return data.background_image ? 'Image set' : 'No hero image';
      case 'about':
        return data.bio ? `${data.bio.substring(0, 30)}...` : 'No bio added';
      case 'gallery':
        const imgCount = data.images?.length || 0;
        return imgCount > 0 ? `${imgCount} images` : 'No images';
      case 'events':
        const eventCount = data.events?.length || 0;
        return eventCount > 0 ? `${eventCount} events` : 'No events';
      case 'news':
        const newsCount = data.items?.length || 0;
        return newsCount > 0 ? `${newsCount} articles` : 'No news';
      case 'music':
        return data.spotify_embed_url ? 'Spotify linked' : 'No music';
      case 'contact':
        return data.email ? 'Contact info set' : 'No contact info';
      case 'social_links':
        const linkCount = data.links?.length || 0;
        return linkCount > 0 ? `${linkCount} links` : 'No links';
      case 'video_gallery':
        const vidCount = data.videos?.length || 0;
        return vidCount > 0 ? `${vidCount} videos` : 'No videos';
      case 'mailing_list':
        return data.signup_url ? 'Form linked' : 'Not set up';
      case 'press':
        const quoteCount = data.quotes?.length || 0;
        return quoteCount > 0 ? `${quoteCount} quotes` : 'No quotes';
      case 'services':
        const serviceCount = data.services?.length || 0;
        return serviceCount > 0 ? `${serviceCount} services` : 'No services';
      case 'team':
        const memberCount = data.members?.length || 0;
        return memberCount > 0 ? `${memberCount} members` : 'No team';
      case 'faq':
        const faqCount = data.faqs?.length || 0;
        return faqCount > 0 ? `${faqCount} FAQs` : 'No FAQs';
      case 'testimonials':
        const testimonialCount = data.testimonials?.length || 0;
        return testimonialCount > 0 ? `${testimonialCount} reviews` : 'No testimonials';
      case 'timetable':
        const groupCount = data.groups?.length || 0;
        return groupCount > 0 ? `${groupCount} schedule columns` : 'No schedule yet';
      case 'retreats':
        const retreatCount = data.retreats?.length || 0;
        return retreatCount > 0 ? `${retreatCount} retreats` : 'No retreats yet';
      default:
        return 'Click to edit';
    }
  };

  // Check if module has content
  const moduleHasContent = (moduleName) => {
    const data = siteData.modules?.[moduleName] || {};
    switch (moduleName) {
      case 'home': return !!data.background_image || !!data.headline;
      case 'about': return !!data.bio || !!data.profile_image;
      case 'gallery': return (data.images?.length || 0) > 0;
      case 'events': return (data.events?.length || 0) > 0;
      case 'news': return (data.items?.length || 0) > 0;
      case 'music': return !!data.spotify_embed_url;
      case 'contact': return !!data.email || !!data.phone;
      case 'social_links': return (data.links?.length || 0) > 0;
      case 'video_gallery': return (data.videos?.length || 0) > 0;
      case 'mailing_list': return !!data.signup_url;
      case 'press': return (data.quotes?.length || 0) > 0;
      case 'services': return (data.services?.length || 0) > 0;
      case 'team': return (data.members?.length || 0) > 0;
      case 'faq': return (data.faqs?.length || 0) > 0;
      case 'testimonials': return (data.testimonials?.length || 0) > 0;
      case 'timetable': return (data.groups?.length || 0) > 0 || (data.prices?.length || 0) > 0;
      case 'retreats': return (data.retreats?.length || 0) > 0;
      default: return false;
    }
  };

  // Module descriptions
  const moduleDescriptions = {
    home: 'Hero section with headline and background',
    about: 'Your bio and profile image',
    gallery: 'Photo gallery showcase',
    events: 'Upcoming shows and tour dates',
    news: 'Latest news and updates',
    music: 'Spotify and Apple Music links',
    contact: 'Contact information and form',
    social_links: 'Social media profiles',
    video_gallery: 'YouTube and video content',
    mailing_list: 'Newsletter signup form',
    press: 'Press quotes and reviews',
    services: 'Services, classes, and offerings',
    team: 'Team members and collaborators',
    faq: 'Frequently asked questions',
    testimonials: 'Client reviews and testimonials',
    timetable: 'Weekly class schedule with pricing',
  };

  // Module gradient colors
  const moduleGradients = {
    home: 'from-violet-500 to-purple-600',
    about: 'from-blue-500 to-cyan-500',
    gallery: 'from-pink-500 to-rose-500',
    events: 'from-amber-500 to-orange-500',
    news: 'from-emerald-500 to-teal-500',
    music: 'from-green-500 to-emerald-500',
    services: 'from-indigo-500 to-blue-500',
    team: 'from-cyan-500 to-teal-500',
    faq: 'from-yellow-500 to-amber-500',
    testimonials: 'from-rose-500 to-pink-500',
    contact: 'from-indigo-500 to-blue-500',
    social_links: 'from-fuchsia-500 to-pink-500',
    video_gallery: 'from-red-500 to-rose-500',
    mailing_list: 'from-cyan-500 to-blue-500',
    press: 'from-yellow-500 to-amber-500',
    timetable: 'from-teal-500 to-emerald-500',
    retreats: 'from-amber-500 to-yellow-600',
  };

  const enabledModules = siteData.enabled_modules || [];

  // If editing a module, show the editor
  if (activeModule) {
    return (
      <div className="min-h-screen bg-slate-900">
        {/* Editor Header */}
        <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-30">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setActiveModule(null)}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back to Dashboard</span>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setBackstageOpen(true)}
                variant="outline"
                className="bg-transparent border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                data-testid="open-backstage-editor-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Backstage
              </Button>
              <Button 
                variant="outline"
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                data-testid="editor-save-btn"
                className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
              </Button>
            </div>
          </div>
        </header>

        {/* Editor Content */}
        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              {(() => {
                const Icon = moduleIcons[activeModule] || Layout;
                return (
                  <div className={cn("w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center", moduleGradients[activeModule])}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                );
              })()}
              <div>
                <h1 className="text-2xl font-bold text-white capitalize">
                  {activeModule.replace('_', ' ')}
                </h1>
                <p className="text-slate-400 text-sm">{moduleDescriptions[activeModule]}</p>
              </div>
            </div>
          </div>
          
          {/* Editor Form Panel - Light inputs for better readability */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 
            [&_input]:bg-white [&_input]:text-slate-900 [&_input]:border-slate-300 [&_input]:placeholder:text-slate-400
            [&_textarea]:bg-white [&_textarea]:text-slate-900 [&_textarea]:border-slate-300 [&_textarea]:placeholder:text-slate-400
            [&_select]:bg-white [&_select]:text-slate-900 [&_select]:border-slate-300
            [&_label]:text-slate-200 [&_label]:font-medium
            [&_.border]:border-slate-600">
            {getModuleEditor(activeModule)}
          </div>
        </main>

        {/* Backstage AI Panel (in editor view) */}
        <BackstagePanel
          siteId={siteData.id}
          siteData={siteData}
          onApplyActions={handleApplyAIActions}
          isOpen={backstageOpen}
          onClose={() => setBackstageOpen(false)}
        />
      </div>
    );
  }

  // Main Dashboard Grid View
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Dashboard Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={onClose}
                className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">{siteData.performer_name}</h1>
                <p className="text-sm text-slate-400">{siteData.slug}.boxit.site</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Backstage AI Button */}
              <Button
                onClick={() => setBackstageOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white border-0"
                data-testid="open-backstage-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Backstage
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(`/sites/${siteData.slug}?preview=true`, '_blank')}
                className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              {siteData.status === 'published' ? (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-medium">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    Live
                  </span>
                  <Button 
                    variant="outline" 
                    onClick={handleUnpublish}
                    className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Unpublish
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handlePublish} 
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white border-0"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Publish Site
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Section Title */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-1">Site Modules</h2>
          <p className="text-slate-400 text-sm">Click any module to edit its content</p>
        </div>

        {/* Module Grid - App Icon Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 justify-items-center max-w-4xl mx-auto">
          {enabledModules.map((moduleName, index) => {
            const Icon = moduleIcons[moduleName] || Layout;
            const hasContent = moduleHasContent(moduleName);
            const gradient = moduleGradients[moduleName] || 'from-slate-500 to-slate-600';
            
            return (
              <button
                key={moduleName}
                onClick={() => setActiveModule(moduleName)}
                data-testid={`module-tile-${moduleName}`}
                className="group flex flex-col items-center gap-4 p-5 rounded-3xl transition-all duration-300 hover:bg-slate-800/30"
                style={{ 
                  animationDelay: `${index * 60}ms`,
                  animation: 'fadeInUp 0.5s ease-out forwards'
                }}
              >
                {/* Large App Icon */}
                <div className={cn(
                  "relative w-28 h-28 rounded-[32px] bg-gradient-to-br flex items-center justify-center transition-all duration-300 ease-out cursor-pointer",
                  "group-hover:scale-115 group-hover:shadow-2xl group-hover:-translate-y-3",
                  "shadow-xl group-active:scale-95",
                  gradient
                )}
                style={{
                  boxShadow: `0 15px 50px -12px ${gradient.includes('violet') ? 'rgba(139, 92, 246, 0.6)' : 
                    gradient.includes('blue') && !gradient.includes('indigo') ? 'rgba(59, 130, 246, 0.6)' :
                    gradient.includes('pink') && !gradient.includes('fuchsia') ? 'rgba(236, 72, 153, 0.6)' :
                    gradient.includes('amber') ? 'rgba(245, 158, 11, 0.6)' :
                    gradient.includes('emerald') ? 'rgba(16, 185, 129, 0.6)' :
                    gradient.includes('green') ? 'rgba(34, 197, 94, 0.6)' :
                    gradient.includes('indigo') ? 'rgba(99, 102, 241, 0.6)' :
                    gradient.includes('fuchsia') ? 'rgba(217, 70, 239, 0.6)' :
                    gradient.includes('red') ? 'rgba(239, 68, 68, 0.6)' :
                    gradient.includes('cyan') ? 'rgba(6, 182, 212, 0.6)' :
                    'rgba(0, 0, 0, 0.4)'}`
                }}
                >
                  <Icon className="w-14 h-14 text-white drop-shadow-lg" strokeWidth={1.5} />
                  
                  {/* Status dot */}
                  {hasContent && (
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-400 rounded-full border-4 border-slate-900 shadow-lg">
                      <span className="absolute inset-1 bg-emerald-300 rounded-full animate-ping opacity-75"></span>
                    </span>
                  )}
                  
                  {/* Glass shine effect */}
                  <div className="absolute inset-0 rounded-[32px] overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
                  </div>
                  
                  {/* Hover glow */}
                  <div className="absolute -inset-2 rounded-[40px] bg-gradient-to-br opacity-0 group-hover:opacity-40 blur-xl transition-opacity duration-300 -z-10"
                    style={{
                      background: gradient.includes('violet') ? 'rgba(139, 92, 246, 0.8)' : 
                        gradient.includes('blue') && !gradient.includes('indigo') ? 'rgba(59, 130, 246, 0.8)' :
                        gradient.includes('pink') && !gradient.includes('fuchsia') ? 'rgba(236, 72, 153, 0.8)' :
                        gradient.includes('amber') ? 'rgba(245, 158, 11, 0.8)' :
                        gradient.includes('emerald') ? 'rgba(16, 185, 129, 0.8)' :
                        gradient.includes('green') ? 'rgba(34, 197, 94, 0.8)' :
                        gradient.includes('indigo') ? 'rgba(99, 102, 241, 0.8)' :
                        gradient.includes('fuchsia') ? 'rgba(217, 70, 239, 0.8)' :
                        gradient.includes('red') ? 'rgba(239, 68, 68, 0.8)' :
                        gradient.includes('cyan') ? 'rgba(6, 182, 212, 0.8)' :
                        'rgba(100, 100, 100, 0.8)'
                    }}
                  />
                </div>
                
                {/* Label */}
                <span className="text-sm font-semibold text-slate-400 group-hover:text-white transition-all duration-300 capitalize text-center leading-tight">
                  {moduleName.replace('_', ' ')}
                </span>
              </button>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Status</p>
            <p className={cn(
              "font-semibold",
              siteData.status === 'published' ? "text-emerald-400" : "text-amber-400"
            )}>
              {siteData.status === 'published' ? 'Published' : 'Draft'}
            </p>
          </div>
          <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Package</p>
            <p className="text-white font-semibold capitalize">{siteData.package}</p>
          </div>
          <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Modules</p>
            <p className="text-white font-semibold">{enabledModules.length} active</p>
          </div>
          <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Theme</p>
            <p className="text-white font-semibold capitalize">{siteData.theme?.style?.replace('_', ' ') || 'Default'}</p>
          </div>
        </div>

        {/* Custom Domain Section */}
        <SEOSettings siteData={siteData} onUpdate={(newData) => setSiteData(prev => ({...prev, ...newData}))} />
        <DomainSettings siteData={siteData} onUpdate={(newData) => setSiteData(prev => ({...prev, ...newData}))} />
      </main>

      {/* Backstage AI Panel */}
      <BackstagePanel
        siteId={siteData.id}
        siteData={siteData}
        onApplyActions={handleApplyAIActions}
        isOpen={backstageOpen}
        onClose={() => setBackstageOpen(false)}
      />

      {/* CSS for animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
